// ============================================================
// store/safetyStore.js
// EV Powertrain Simulator — 안전/보안 전역 상태
// 패턴: 싱글톤(Singleton) — safetyStore 인스턴스를 직접 export
// 사용법: import { safetyStore, useSafetyStore } from './safetyStore.js';
// ISO 26262 V-모델 탭 구조 정렬 (2026-04)
//
// 탭 ↔ 상태 매핑 (Phase 3 기준 — 9탭 V-model 구조):
//   ① hara                    → haraItems, safetyGoals
//   ② technical_safety_concept → (TSR: generateFSR_TSR 로컬 — store 불필요)
//   ③ fmea_safety              → faultModes, ftaOverrides
//   ④ hw_safety                → fitData (Part 5)
//   ⑤ sw_safety                → part6Items, vvChecklist (Part 6)
//   ⑥ cybersecurity_tara       → taraAssets
//   ⑦ sotif_iso21448           → (SOTIFTabContent 로컬)
//   ⑧ traceability_case        → reviewStatus, evidenceLinks, changeHistory
//   ⑨ safety_verification      → (SafetyVerificationTabContent 로컬)
//
// 사용법:
//   import { safetyStore } from './safetyStore.js';
//   import { useSafetyStore } from './safetyStore.js';
//
//   // 컴포넌트 내부
//   const faultModes = useSafetyStore(s => s.faultModes);
//   const { updateFaultMode } = useSafetyStore(s => s);
// ============================================================

import { useState, useEffect, useSyncExternalStore } from 'react';
import { FAULT_MODES } from '../constants/FAULT_MODES.js';

// ── LocalStorage 어댑터 ────────────────────────────────────
// [설계 의도] 안전/보안 데이터는 HARA 오버라이드, V&V 체크리스트, 침투 테스트 결과 등
// 사용자가 직접 입력한 분석 데이터를 포함하므로 페이지 새로고침 후에도 보존해야 한다.
// 반면 tsrList, hwSafetyResult, sotifResult 등 계산값은 영속화 제외해 항상 최신 파라미터 기준으로 재계산한다.
const SAFETY_STORAGE_KEY = 'ev-sim-safety-v1';

/**
 * localStorage에서 안전/보안 상태 스냅샷을 읽어 반환한다.
 *
 * localStorage 미지원 환경(SSR, 아티팩트 샌드박스)에서는 null을 반환하고
 * 호출부가 초기값으로 폴백하도록 한다.
 *
 * @returns {object|null}  저장된 안전 상태 스냅샷 (없으면 null)
 */
function loadSafetyFromStorage() {
  try {
    const raw = localStorage.getItem(SAFETY_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/**
 * 안전/보안 상태 중 사용자 입력 데이터만 localStorage에 직렬화해 저장한다.
 *
 * 계산값(tsrList, hwSafetyResult, sotifResult, taraResult, swPart6Result, faultInjResults)은
 * params 변경 시 자동 재계산되므로 영속화 대상에서 제외한다.
 * localStorage가 없는 환경에서는 조용히 무시하며 인메모리 상태는 정상 유지된다.
 *
 * @param {object} state  현재 safetyStore 상태 객체
 * @returns {boolean}     저장 성공 여부
 */
function saveSafetyToStorage(state) {
  // [Fix-⑦] localStorage는 페이지 새로고침 시 영속성 용도 전용
  // 컴포넌트 간 실시간 공유는 반드시 safetyStore.subscribe / useSafetyStore()로 처리할 것
  // 아티팩트/샌드박스 환경에서 localStorage가 없어도 store의 인메모리 상태는 정상 동작
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(SAFETY_STORAGE_KEY, JSON.stringify({
      faultModes:     state.faultModes,
      reviewStatus:   state.reviewStatus,
      evidenceLinks:  state.evidenceLinks,
      changeHistory:  state.changeHistory,
      vvChecklist:    state.vvChecklist,
      pentestResults: state.pentestResults,  // GAP-4: 사이버보안 침투테스트 영속화
      haraOverrides:  state.haraOverrides,   // ① HARA S/E/C 편집값 영속화
      savedAt:        new Date().toISOString(),
    }));
    return true;
  } catch { return false; } // silent fail — in-memory state remains valid
}

// ── V&V 체크리스트 초기값 (ISO 26262 Part 6) ───────────────
const VV_CHECKLIST_INITIAL = [
  { id: 'VV-SW-01', part: 'Part 6', item: '소프트웨어 안전 요구사항 명세', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-SW-02', part: 'Part 6', item: '소프트웨어 아키텍처 설계', status: 'Draft', asil: 'C', reviewer: '' },
  { id: 'VV-SW-03', part: 'Part 6', item: '단위 설계 및 구현', status: 'Draft', asil: 'C', reviewer: '' },
  { id: 'VV-SW-04', part: 'Part 6', item: '단위 테스트 (Statement/Branch Coverage)', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-SW-05', part: 'Part 6', item: '통합 테스트', status: 'Draft', asil: 'C', reviewer: '' },
  { id: 'VV-SW-06', part: 'Part 6', item: '검증 테스트 (ASIL D 100% 커버리지)', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-HW-01', part: 'Part 5', item: 'HW 안전 요구사항 명세', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-HW-02', part: 'Part 5', item: 'FIT/λ 데이터 수집 및 검증', status: 'Draft', asil: 'B', reviewer: '' },
  { id: 'VV-HW-03', part: 'Part 5', item: 'PMHF 목표값 예산 배분', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-HW-04', part: 'Part 5', item: 'DFA (종속고장분석) 완료', status: 'Draft', asil: 'C', reviewer: '' },
  { id: 'VV-SY-01', part: 'Part 4', item: '안전 목표(SG) 수립', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-SY-02', part: 'Part 4', item: 'FSR 도출 및 검토', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-SY-03', part: 'Part 4', item: 'TSR 도출 및 검토', status: 'Draft', asil: 'C', reviewer: '' },
  { id: 'VV-SY-04', part: 'Part 4', item: 'FMEA / FTA / DFA 완료', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-SY-05', part: 'Part 4', item: '안전 메커니즘(SM) 커버리지 검증', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-IT-01', part: 'Part 4', item: '기능안전 감사 (FSA)', status: 'Draft', asil: 'D', reviewer: '' },
  { id: 'VV-IT-02', part: 'Part 4', item: '기능안전 평가 (FSAssessment)', status: 'Draft', asil: 'D', reviewer: '' },
];

// ── 리뷰 상태 초기값 생성 ──────────────────────────────────
/**
 * FAULT_MODES 배열을 순회하여 모든 고장 모드의 초기 검토 상태를 'Draft'로 설정한다.
 *
 * @returns {{ [fmId: string]: 'Draft'|'Review'|'Approved'|'Closed' }}
 */
function buildInitialReviewStatus() {
  const init = {};
  FAULT_MODES.forEach(fm => { init[fm.id] = 'Draft'; });
  return init;
}

// ── 스토어 초기 상태 ────────────────────────────────────────
/**
 * localStorage 저장값과 FAULT_MODES 원본을 머지하여 안전 스토어 초기 상태를 구성한다.
 *
 * 머지 전략:
 *   - faultModes: FAULT_MODES 원본을 기준으로 저장값을 id 기준 머지 → 신규 모드 자동 반영
 *   - vvChecklist: VV_CHECKLIST_INITIAL 원본에 저장값을 id 기준 머지 → 항목 추가 시 자동 반영
 *   - haraOverrides, reviewStatus, evidenceLinks: 저장값 그대로 복원
 *   - 계산값(sotifResult, taraResult 등): 항상 null로 초기화 (재계산 필요)
 *
 * @returns {object}  safetyStore 초기 상태 객체
 */
function buildInitialState() {
  const saved = loadSafetyFromStorage();

  return {
    // ② 시스템 안전 분석 — 편집 가능 고장 모드
    faultModes: saved?.faultModes
      ? FAULT_MODES.map(orig => {
          const s = saved.faultModes.find(f => f.id === orig.id);
          return s ? { ...orig, ...s } : { ...orig };
        })
      : FAULT_MODES.map(fm => ({ ...fm })),

    // FTA 게이트 오버라이드
    ftaGateOverrides: {},

    // ① HARA S/E/C 인터랙티브 편집값 — { [fmId]: { S?, E?, C? } }
    // 원천: HARATabContent → setHaraOverride()
    // 소비: HARATabContent → analyzeHARA(params, haraOverrides)
    haraOverrides: saved?.haraOverrides ?? {},

    // ⑥ 검토 상태 워크플로
    reviewStatus: saved?.reviewStatus ?? buildInitialReviewStatus(),

    // ⑥ 증거 첨부
    evidenceLinks: saved?.evidenceLinks ?? {},

    // ⑥ 변경 이력
    changeHistory: saved?.changeHistory ?? [],

    // ③ V&V 체크리스트
    vvChecklist: saved?.vvChecklist
      ? VV_CHECKLIST_INITIAL.map(orig => {
          const s = saved.vvChecklist.find(v => v.id === orig.id);
          return s ? { ...orig, ...s } : { ...orig };
        })
      : VV_CHECKLIST_INITIAL.map(v => ({ ...v })),

    // ① 개념 단계 — HARA에서 피드
    haraItems:   [],   // HARATabContent에서 analyzeHARA() 결과 push
    safetyGoals: [],   // generateFSR_TSR() 결과 push

    // ② 기술안전 개념 — TechnicalSafetyConceptTabContent에서 write
    // 원천: TechnicalSafetyConceptTabContent → setTsrList()
    // 소비: TraceabilityTabContent → TSR 추적성 요약
    tsrList: [],  // generateFSR_TSR() 결과 전체

    // ③ HW 안전 설계 — HWSafetyTabContent에서 write, Traceability에서 read
    // GAP-1/GAP-3 수정: hsrList / systemASIL 추적성 연결
    hwSafetyResult: null, // { hsr_list, systemASIL, system_asil_pass, SPFM_pct, LFM_pct, PMHF }

    // ④ 사이버보안 — CybersecurityTabContent pentest 영속화
    // GAP-4 수정: 로컬 state → safetyStore 이관으로 탭 전환 후에도 데이터 유지
    // { [threatId]: { result, finding, cvss, tester, notes, updatedAt } }
    pentestResults: saved?.pentestResults ?? {},

    // ⑤·⑥ B3-Fix: 탭 간 계산 결과 공유 (비영속, params 변경 시 재계산)
    // 원천: SOTIFTabContent → setSotifResult()
    //        CybersecurityTabContent → setTaraResult()
    // 소비: TraceabilityTabContent → computeSafetyTriangle(haraResults, taraResult, sotifResult)
    sotifResult: null,
    taraResult:  null,

    // ⑤ SW 안전 설계 — SWSafetyTabContent에서 write, Traceability에서 read
    // 원천: SWSafetyTabContent → setSwPart6Result()
    // 소비: TraceabilityTabContent → SW 커버리지 요약 표시
    swPart6Result: null, // computeISO26262_Part6() 결과 전체

    // ⑨ Safety V&V — SafetyVerificationTabContent에서 write, Traceability에서 read
    // 원천: SafetyVerificationTabContent → setFaultInjResults()
    // 소비: TraceabilityTabContent → V&V 체크리스트 PASS/FAIL 배지
    faultInjResults: {}, // { [hazardId]: { mean, p95, p99, safety_margin, pass, N } }
  };
}

// ── 리뷰 순서 ──────────────────────────────────────────────
export const REVIEW_ORDER  = ['Draft', 'Review', 'Approved', 'Closed'];
export const REVIEW_COLORS = { Draft: '#94a3b8', Review: '#f59e0b', Approved: '#22c55e', Closed: '#6366f1' };
export const REVIEW_LABELS = { Draft: '📝 Draft', Review: '🔍 검토중', Approved: '✅ 승인', Closed: '🔒 완료' };

// ── 변경 이력 ID 카운터 ────────────────────────────────────
// [설계 의도] 모듈 스코프 카운터를 사용해 싱글톤 인스턴스와 생명주기를 일치시킨다.
// 페이지 새로고침 시 0으로 리셋되지만 changeHistory 자체는 localStorage에서 복원된다.
let _chIdCounter = 0;

// ── 스토어 팩토리 ──────────────────────────────────────────
/**
 * 안전/보안 전역 스토어 인스턴스를 생성한다.
 *
 * ISO 26262 V-모델 9탭 구조를 지원하며, 각 탭의 계산 결과·사용자 편집값·
 * 리뷰 워크플로를 단일 상태 트리로 관리한다.
 *
 * 영속화 전략:
 *   - _persist() 호출 시 saveSafetyToStorage()를 통해 localStorage에 저장
 *   - 사용자 편집값(faultModes, haraOverrides, reviewStatus, evidenceLinks,
 *     vvChecklist, pentestResults, changeHistory)은 영속화
 *   - 계산값(tsrList, hwSafetyResult, sotifResult, taraResult,
 *     swPart6Result, faultInjResults)은 메모리 전용
 *
 * @returns {object}  safetyStore 인스턴스
 */
function createSafetyStore() {
  let state = {
    ...buildInitialState(),

    // ══ 액션: faultModes ══════════════════════════════════

    /** 단일 고장 모드 필드 수치 업데이트 (severity/occurrence/detection 1~10) */
    updateFaultMode(fmId, field, rawValue) {
      const numVal = Math.max(1, Math.min(10, parseInt(rawValue, 10) || 1));
      const old = state.faultModes.find(f => f.id === fmId)?.[field];
      state.faultModes = state.faultModes.map(fm =>
        fm.id === fmId ? { ...fm, [field]: numVal } : fm
      );
      state._addChange(fmId, field, old ?? '—', numVal);
      state._persist();
      state._notify();
    },

    /** faultModes 전체 교체 (import 등) */
    setFaultModes(modes) {
      state.faultModes = modes;
      state._persist();
      state._notify();
    },

    // ══ 액션: haraOverrides (① HARA S/E/C 인터랙티브 편집) ══

    /**
     * HARA 항목의 S/E/C 값을 오버라이드
     * @param {string} fmId  - FAULT_MODES id (예: 'FM-01')
     * @param {'S'|'E'|'C'} field
     * @param {number} value - S: 1~3, E: 1~4, C: 1~3
     */
    setHaraOverride(fmId, field, value) {
      const prev = (state.haraOverrides[fmId] || {})[field];
      const fm = state.faultModes.find(f => f.id === fmId);
      const orig = fm?.[field];
      state.haraOverrides = {
        ...state.haraOverrides,
        [fmId]: { ...(state.haraOverrides[fmId] || {}), [field]: value },
      };
      state._addChange(fmId, `HARA.${field}`, prev ?? orig ?? '?', value);
      state._persist();
      state._notify();
    },

    /** HARA 오버라이드 단일 항목 전체 초기화 */
    resetHaraOverride(fmId) {
      const { [fmId]: _, ...rest } = state.haraOverrides;
      state.haraOverrides = rest;
      state._addChange(fmId, 'HARA.reset', '편집값', '원본');
      state._persist();
      state._notify();
    },

    /** HARA 오버라이드 전체 초기화 */
    resetAllHaraOverrides() {
      state.haraOverrides = {};
      state._addChange('ALL', 'HARA.reset_all', '편집값', '원본');
      state._persist();
      state._notify();
    },

    /** FTA 게이트 오버라이드 */
    setFtaGateOverride(gateId, type) {
      if (type === null) {
        const { [gateId]: _, ...rest } = state.ftaGateOverrides;
        state.ftaGateOverrides = rest;
      } else {
        state.ftaGateOverrides = { ...state.ftaGateOverrides, [gateId]: type };
      }
      state._notify();
    },

    resetFtaGateOverrides() {
      state.ftaGateOverrides = {};
      state._notify();
    },

    // ══ 액션: sotifResult / taraResult (B3-Fix) ══════════
    // 계산 원천: SOTIFTabContent(sotif) / CybersecurityTabContent(tara)
    // 소비처: TraceabilityTabContent → computeSafetyTriangle
    // NOTE: 이 두 필드는 영속화하지 않음(params 변경 시 재계산)

    setSotifResult(result) {
      state.sotifResult = result;
      state._notify();
    },

    setTaraResult(result) {
      state.taraResult = result;
      state._notify();
    },

    setSwPart6Result(result) {
      state.swPart6Result = result;
      state._notify();
      // swPart6Result는 계산값이므로 localStorage 영속화 제외 (재계산 가능)
    },

    setFaultInjResults(results) {
      state.faultInjResults = results ?? {};
      state._notify();
      // faultInjResults는 런타임 계산값 → localStorage 영속화 제외
    },

    clearChangeHistory() {
      state.changeHistory = [];
      state._persist();
      state._notify();
    },

    // ══ 액션: reviewStatus ════════════════════════════════

    /** 다음 리뷰 단계로 진행 */
    advanceReview(id) {
      const cur  = state.reviewStatus[id] || 'Draft';
      const idx  = REVIEW_ORDER.indexOf(cur);
      const next = REVIEW_ORDER[Math.min(idx + 1, REVIEW_ORDER.length - 1)];
      state.reviewStatus = { ...state.reviewStatus, [id]: next };
      state._addChange(id, '검토상태', cur, next);
      state._persist();
      state._notify();
    },

    /** 리뷰 상태 직접 설정 */
    setReviewStatus(id, status) {
      const cur = state.reviewStatus[id] || 'Draft';
      state.reviewStatus = { ...state.reviewStatus, [id]: status };
      state._addChange(id, '검토상태', cur, status);
      state._persist();
      state._notify();
    },

    // ══ 액션: evidenceLinks ═══════════════════════════════

    /** 증거 첨부 추가 */
    addEvidence(fmId, url, desc = '첨부 파일') {
      if (!fmId || !url) return;
      const ev = {
        id:    'EV-' + Date.now(),
        url,
        desc,
        added: new Date().toLocaleDateString('ko-KR'),
      };
      state.evidenceLinks = {
        ...state.evidenceLinks,
        [fmId]: [...(state.evidenceLinks[fmId] || []), ev],
      };
      state._addChange(fmId, '증거첨부', '—', url);
      state._persist();
      state._notify();
    },

    /** 증거 삭제 */
    removeEvidence(fmId, evId) {
      const list = (state.evidenceLinks[fmId] || []).filter(e => e.id !== evId);
      state.evidenceLinks = { ...state.evidenceLinks, [fmId]: list };
      state._persist();
      state._notify();
    },

    // ══ 액션: vvChecklist ════════════════════════════════

    /** V&V 항목 상태 업데이트 */
    updateVVItem(vvId, field, value) {
      state.vvChecklist = state.vvChecklist.map(v =>
        v.id === vvId ? { ...v, [field]: value } : v
      );
      state._persist();
      state._notify();
    },

    // ══ 액션: HARA 피드 ══════════════════════════════════

    /** HARATabContent에서 분석 결과 push */
    feedHara(haraItems, safetyGoals) {
      state.haraItems   = haraItems   ?? state.haraItems;
      state.safetyGoals = safetyGoals ?? state.safetyGoals;
      state._notify();
    },

    /** TechnicalSafetyConceptTabContent에서 TSR 목록 공유 */
    setTsrList(list) {
      state.tsrList = list ?? [];
      state._notify();
      // tsrList는 계산값 → localStorage 영속화 제외
    },

    // ══ 액션: HW Safety Result (GAP-1 / GAP-3 수정) ══════

    /**
     * HWSafetyTabContent에서 computeHWSafety() 결과를 저장
     * Traceability 탭이 HSR Pass/Fail + ASIL 수준을 읽어 추적성 매트릭스에 반영
     */
    setHwSafetyResult(result) {
      state.hwSafetyResult = result;
      state._notify();
      // hwSafetyResult는 계산값이므로 localStorage 영속화 제외 (재계산 가능)
    },

    // ══ 액션: pentestResults (GAP-4 수정) ════════════════

    /**
     * 침투 테스트 단일 항목 필드 업데이트
     * @param {string} threatId  - calMatrix 위협 ID (예: 'TH-001')
     * @param {string} field     - 'result' | 'finding' | 'cvss' | 'tester' | 'notes'
     * @param {*}      value     - 새 값
     */
    updatePentest(threatId, field, value) {
      state.pentestResults = {
        ...state.pentestResults,
        [threatId]: {
          ...(state.pentestResults[threatId] || {}),
          [field]: value,
          updatedAt: new Date().toISOString(),
        },
      };
      state._persist();
      state._notify();
    },

    /**
     * 침투 테스트 결과 전체 교체 (CSV 가져오기 등)
     */
    setPentestResults(results) {
      state.pentestResults = results ?? {};
      state._persist();
      state._notify();
    },

    /**
     * 침투 테스트 결과 전체 초기화
     */
    clearPentestResults() {
      state.pentestResults = {};
      state._persist();
      state._notify();
    },

    // ══ 내부: persist / notify / subscribe ═══════════════

    /**
     * 변경 이력 항목을 생성하여 changeHistory 맨 앞에 삽입한다.
     *
     * 최대 200건을 유지하며 초과 시 오래된 항목을 자동으로 삭제한다.
     * 모든 편집 액션(_addChange를 내부 호출)에서 추적 가능하다.
     *
     * @param {string} item_id   변경 대상 항목 ID (예: 'FM-01', 'ALL')
     * @param {string} field     변경 필드명 (예: 'severity', 'HARA.S')
     * @param {*}      old_val   이전 값
     * @param {*}      new_val   새 값
     */
    _addChange(item_id, field, old_val, new_val) {
      const id = 'CH-' + String(++_chIdCounter).padStart(4, '0');
      const ts = new Date().toLocaleString('ko-KR');
      state.changeHistory = [
        { id, item_id, field,
          old_val: String(old_val), new_val: String(new_val),
          timestamp: ts, author: '현재 사용자', status: 'Draft' },
        ...state.changeHistory,
      ].slice(0, 200); // 최대 200건
    },

    /** 현재 사용자 입력 데이터를 localStorage에 저장한다. */
    _persist() { saveSafetyToStorage(state); },

    _listeners: new Set(),

    /** 등록된 모든 구독자에게 현재 상태를 동기 전달한다. */
    _notify() {
      state._listeners.forEach(fn => fn(state));
    },

    /**
     * 상태 변경 구독을 등록하고, 해제 함수를 반환한다.
     * @param {(state: object) => void} fn  상태 변경 콜백
     * @returns {() => void}                구독 해제 함수
     */
    subscribe(fn) {
      state._listeners.add(fn);
      return () => state._listeners.delete(fn);
    },

    /**
     * 현재 스토어 상태 스냅샷을 반환한다.
     * @returns {object}
     */
    getState() { return state; },
  };

  return state;
}

// ── 싱글톤 ──────────────────────────────────────────────────
// [설계 의도] safetyStore는 ISO 26262 V-모델 전체 탭이 공유하는 단일 인스턴스다.
// 팩토리를 사용하지 않고 모듈 로드 시 즉시 생성·export 하는 이유는
// 여러 탭이 동일 분석 결과를 실시간으로 참조·갱신해야 하기 때문이다.
export const safetyStore = createSafetyStore();

// ── React 훅 ────────────────────────────────────────────────

/**
 * safetyStore에서 선택한 상태 슬라이스를 구독하는 React 훅.
 *
 * React 18+에서는 useSyncExternalStore를 사용해 Concurrent 모드에서의
 * tearing(상태 불일치) 문제를 방지한다.
 * React 17 이하에서는 useState + useEffect 조합으로 폴백한다.
 *
 * 사용 예:
 *   const faultModes = useSafetyStore(s => s.faultModes);
 *   const { updateFaultMode } = useSafetyStore(s => s);
 *
 * @template T
 * @param {(state: object) => T} selector  상태에서 필요한 슬라이스를 추출하는 셀렉터 함수
 * @returns {T}  셀렉터가 반환하는 상태 슬라이스 (변경 시 리렌더링 트리거)
 */
export function useSafetyStore(selector) {
  if (typeof useSyncExternalStore !== 'undefined') {
    // [React 18+] useSyncExternalStore: Concurrent 모드 tearing 방지
    return useSyncExternalStore(
      safetyStore.subscribe,
      () => selector(safetyStore.getState()),
    );
  }
  // React 17 fallback
  const [snap, setSnap] = useState(() => selector(safetyStore.getState()));
  useEffect(() => {
    setSnap(selector(safetyStore.getState()));
    const unsub = safetyStore.subscribe(s => setSnap(selector(s)));
    return unsub;
  }, []);
  return snap;
}

export { VV_CHECKLIST_INITIAL };